
(function () {
  if (customElements.get('silo-testimonials')) return;

  const DESKTOP_MIN = 1280;
  const VIDEO_CLASS =
    'card-video t-w-full t-h-full t-object-cover t-rounded-t-lg t-rounded-bl-[40px] t-rounded-br-[40px]';

  class SiloTestimonials extends HTMLElement {
    connectedCallback() {
      this.cleanups = [];
      this.observers = [];
      this.swiper = null;
      this.booted = false;

      // Lazy boot — don't build Swiper/observers until the section is near view.
      this.bootObserver = new IntersectionObserver(
        (entries) => {
          if (entries.some((e) => e.isIntersecting)) {
            this.boot();
            this.bootObserver?.disconnect();
            this.bootObserver = null;
          }
        },
        { rootMargin: '200px' },
      );
      this.bootObserver.observe(this);
    }

    disconnectedCallback() {
      this.bootObserver?.disconnect();
      this.bootObserver = null;
      clearTimeout(this._swiperRetry);
      this.observers.forEach((o) => o.disconnect());
      this.observers = [];
      this.cleanups.forEach((off) => off());
      this.cleanups = [];
      this.swiper?.destroy?.(true, true);
      this.swiper = null;
    }

    boot() {
      if (this.booted) return;
      this.booted = true;
      // The user is about to reach the section — covers must not still be lazy
      // (a swipe would land on a slide whose cover hasn't been fetched: black flash).
      this.querySelectorAll('img.video-cover-image[loading="lazy"]').forEach((img) => {
        img.loading = 'eager';
      });
      this.initSwiper();
    }

    on(target, type, handler, options) {
      target.addEventListener(type, handler, options);
      this.cleanups.push(() => target.removeEventListener(type, handler, options));
    }

    /* -------------------------------- swiper -------------------------------- */

    initSwiper() {
      const el = this.querySelector('.testimonialSwiper');
      if (!el) {
        this.initMediaPlayback();
        return;
      }

      // Swiper is a deferred CDN global. On section rendering the element can
      // boot before that script has parsed — wait for it instead of bailing.
      if (typeof Swiper === 'undefined') {
        this._swiperTries = (this._swiperTries || 0) + 1;
        if (this._swiperTries > 60) {
          this.initMediaPlayback(); // degrade gracefully: covers still show, desktop hover still works
          return;
        }
        this._swiperRetry = setTimeout(() => this.initSwiper(), 50);
        return;
      }

      const isMdToSlg = window.innerWidth >= 750 && window.innerWidth < 1280;

      this.swiper = new Swiper(el, {
        slidesPerView: 'auto',
        centeredSlides: isMdToSlg,
        spaceBetween: 16,
        initialSlide: isMdToSlg ? 1 : 0,
        grabCursor: true,
        loop: true,
      });

      this.on(
        window,
        'resize',
        () => {
          const md = window.innerWidth >= 768 && window.innerWidth < 1280;
          if (md && this.swiper.activeIndex === 0) this.swiper.slideTo(1);
          else if (!md && this.swiper.activeIndex === 1) this.swiper.slideTo(0);
        },
        { passive: true },
      );

      this.updateActiveCard();
      this.swiper.on('slideChange', () => {
        this.updateActiveCard();
        this.playActiveSlide();
      });

      // Wire playback AFTER Swiper exists: with loop:true Swiper clones slides, and
      // observers attached earlier would miss the clones entirely (never-playing cards).
      this.initMediaPlayback();
    }

    updateActiveCard() {
      this.querySelectorAll('.mobile-card').forEach((c) => c.classList.remove('active-card'));
      const active = this.swiper?.slides?.[this.swiper.activeIndex];
      active?.querySelector('.mobile-card')?.classList.add('active-card');
    }

    playActiveSlide() {
      const active = this.swiper?.slides?.[this.swiper.activeIndex];
      const container = active?.querySelector('.video-container');

      // Pause the other slides (keep their videos: swiping back reuses the buffered
      // element instead of re-downloading — no reload "stacking", no black re-buffer).
      // Removing .playing fades each cover back OVER its paused video.
      this.swiper.slides.forEach((slide) => {
        if (slide === active) return;
        const other = slide.querySelector('.video-container video');
        if (other) {
          other.pause();
          other.closest('.video-container')?.classList.remove('playing');
        }
      });

      if (!container) return;
      const video = this.buildVideo(container, true);
      if (video && video.paused) video.play().catch(() => {});
    }

    /* ---------------------------- media playback ---------------------------- */

    initMediaPlayback() {
      const containers = this.querySelectorAll('.video-container');
      if (!containers.length) return;

      if (window.innerWidth < DESKTOP_MIN) {
        this.initInViewPlayback(containers);
      } else {
        this.initHoverPlayback(containers);
      }
    }

    // Mobile: play the card video while its container is on screen. The cover is NOT
    // hidden here — wireRevealOnPlayback() flips it only once the video actually has
    // frames (the 'playing' media event), so the user sees cover → video, never black.
    initInViewPlayback(containers) {
      const io = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            const container = entry.target;
            const video = container.querySelector('.card-video');
            if (!video) continue;
            if (entry.isIntersecting) {
              video.play().catch(() => {
                container.addEventListener('touchstart', () => video.play().catch(() => {}), {
                  once: true,
                });
              });
            } else {
              video.pause();
              container.classList.remove('playing');
            }
          }
        },
        { threshold: 0.25 },
      );
      containers.forEach((c) => io.observe(c));
      this.observers.push(io);
    }

    // Desktop: build the short clip once on first hover, reuse it, widen the card.
    initHoverPlayback(containers) {
      containers.forEach((container) => {
        const src = container.dataset.short || container.dataset.video;
        if (!src) return;

        let video = null;
        const ensureVideo = () => {
          if (video) return video;
          // Reuse the server-rendered short-clip <video> when present — appending a
          // second element made every hovered card carry two videos.
          video = container.querySelector('video.card-video') || this.createVideoEl(src, true);
          if (!video.parentElement) container.appendChild(video);
          this.wireRevealOnPlayback(container, video);
          return video;
        };

        this.on(container, 'mouseenter', () => {
          const el = ensureVideo();
          el.style.display = '';
          el.currentTime = 0;
          // The cover stays until the clip actually renders ('playing' via wireRevealOnPlayback).
          el.play().catch(() => {});
          container.closest('.desktop-card')?.classList.add('active-card');
        });

        this.on(container, 'mouseleave', () => {
          if (video) {
            video.pause();
            video.currentTime = 0;
            video.style.display = 'none';
          }
          container.classList.remove('playing');
          container.closest('.desktop-card')?.classList.remove('active-card');
        });
      });
    }

    /* -------------------------------- helpers ------------------------------- */

    buildVideo(container, useShort) {
      const src = useShort ? container.dataset.short || container.dataset.video : container.dataset.video;
      if (!src) return null;

      // Reuse the existing element when the source matches — rebuilding on every
      // slideChange re-downloaded the video each swipe.
      const existing = container.querySelector('video');
      if (existing && existing.dataset.src === src) return existing;
      if (existing) existing.remove();

      const video = this.createVideoEl(src, useShort);
      video.dataset.src = src;

      // The cover doubles as the video's poster: even mid-fade the frame area
      // always shows the same image — no gap.
      const cover = container.querySelector('.video-cover-image');
      if (cover && (cover.currentSrc || cover.src)) video.poster = cover.currentSrc || cover.src;

      this.wireRevealOnPlayback(container, video);
      container.appendChild(video);
      return video;
    }

    // The cover hides ONLY when the video truly renders frames ('playing' fires) —
    // never on intent. This is what kills the black flash on swipe.
    wireRevealOnPlayback(container, video) {
      video.addEventListener('playing', () => container.classList.add('playing'));
    }

    createVideoEl(src, loop) {
      const video = document.createElement('video');
      video.className = VIDEO_CLASS;
      video.loop = !!loop;
      video.muted = true;
      video.playsInline = true;
      video.controls = false;
      // preload='none' on the single factory so merely BUILDING a video never triggers a
      // network fetch (the browser default is 'metadata', which fetched the testimonial
      // videos as soon as the below-the-fold section booted). Only an explicit .play()
      // — i.e. the card actually scrolled into view / hovered — loads the video.
      video.preload = 'none';
      const source = document.createElement('source');
      source.src = src;
      source.type = 'video/mp4';
      video.appendChild(source);
      return video;
    }
  }

  customElements.define('silo-testimonials', SiloTestimonials);
})();
